/**
 * COIN DISPLAY FORMATTING — the single place that turns a coin number into text.
 *
 * Coins are tracked as a FLOAT internally and that precision is deliberate: the power pole's
 * `gridUsageCost()` pro-rates a standing charge by `dtMs / DAY_MS` and adds a kWh cost, so it
 * bills a fractional amount continuously. Rounding the ACCRUAL would either drop the charge
 * entirely (tiny ticks round to 0) or over-bill (tiny ticks round to 1). So the sim keeps the
 * fraction and the DISPLAY hides it — every coin figure the player reads goes through here.
 *
 * Two functions, because the honest rounding direction depends on which side of the ledger a
 * number sits on. Never show a player a coin figure that flatters them:
 *
 *   coinText(n)  — a BALANCE or a payout the player RECEIVES. FLOORS. 41.97 gold reads "41",
 *                  because 41 is what they can actually spend; showing "42" invites a purchase
 *                  the game will refuse.
 *   costText(n)  — a BILL, PRICE or amount OWED. CEILS. A 0.4-coin bill reads "1", not "0",
 *                  because a bill that renders as zero but blocks a payment is a bug report.
 *
 * Both are locale-grouped ("1,234") like the raw `toLocaleString()` calls they replace, and both
 * clamp `NaN`/`Infinity` to 0 so a corrupted save renders a number rather than "NaN".
 */

/** Guard: coerce anything non-finite to 0 so a bad save never renders "NaN" in the HUD. */
function finite(n: number): number {
  return Number.isFinite(n) ? n : 0;
}

/**
 * Format a coin BALANCE (or a payout the player receives) for display: FLOOR, then group.
 * Flooring is the honest direction for a balance — the player can never spend the fraction, so
 * showing it rounded up would advertise gold that isn't there.
 *
 * Negative balances floor away from zero (-0.2 → "-1"), which likewise never overstates.
 */
export function coinText(coins: number): string {
  return Math.floor(finite(coins)).toLocaleString();
}

/**
 * Format a coin COST (a bill, a price, an amount owed) for display: CEIL, then group.
 * Ceiling is the honest direction for a debt — a fractional bill that displays as "0" while the
 * Pay button stays live reads as broken.
 */
export function costText(coins: number): string {
  return Math.ceil(finite(coins)).toLocaleString();
}

/**
 * The integer a coin BALANCE displays as. Same flooring as `coinText`, but as a number — for the
 * HUD's animated `coinsDisplay` tween, which must not render a fractional frame mid-flight, and
 * for any caller composing its own string.
 */
export function coinValue(coins: number): number {
  return Math.floor(finite(coins));
}
